import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SvgCart, SvgPlayFilled } from '../../../icons';
import { Asset, EpisodeAsset, MovieAsset } from '../../../queries/ats/titles/types/asset';
import { VodSource } from '../../../queries/ats/titles/types/sources';
import { routes } from '../../../routes/route-utils';
import { useAuth } from '../../../utils/auth-utils';
import { invariant } from '../../../utils/commonUtils';
import { useDetails } from '../details-context';
import { FocusedAsset } from '../use-focused-asset';
import { appendSequenceNumbers } from '../utils';
import { CallToAction } from './call-to-action-button';
import { ContinueWatchingButton } from './continue-watching-button';
import { getProgress } from './utils';

interface VodCallToActionProps {
  asset: Asset;
  focusedAsset: FocusedAsset;
  source: VodSource;
}

export function VodCallToAction({ asset, focusedAsset, source }: VodCallToActionProps) {
  const { setIsTransactionModalOpen } = useDetails('VodCallToAction');
  const { isGuest, loggedInWithCredentials } = useAuth();
  const { t } = useTranslation();

  if (source.product.status === 'purchased' || source.product.status === 'rented') {
    return <OwnedVodCallToAction focusedAsset={focusedAsset} source={source} />;
  }

  if (asset.type === 'series') {
    invariant(focusedAsset.type === 'episode' || focusedAsset.type === 'movie', 'Invalid asset type');
    return <VodSeriesCallToAction focusedAsset={focusedAsset} source={source} />;
  }

  if (isGuest || !loggedInWithCredentials) {
    return <VodGuestCallToAction focusedAsset={focusedAsset} source={source} />;
  }

  return (
    <CallToAction
      text={{
        primary: appendSequenceNumbers(t('details:watch-now'), focusedAsset),
        secondary: t('common:from-price', { value: source.product.price?.amount ?? '???' }),
      }}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgCart />}
      onClick={() => setIsTransactionModalOpen(true)}
    />
  );
}

interface VodSeriesCallToActionProps {
  focusedAsset: EpisodeAsset | MovieAsset;
  source: VodSource;
}

export function VodSeriesCallToAction({ focusedAsset, source }: VodSeriesCallToActionProps) {
  const { setIsTransactionModalOpen } = useDetails('VodCallToAction');
  const { loggedInWithCredentials: isAuth } = useAuth();
  const { t } = useTranslation();

  if (source.product.status === 'purchased' || source.product.status === 'rented') {
    return <OwnedVodCallToAction focusedAsset={focusedAsset} source={source} />;
  }

  if (!isAuth) {
    return <VodGuestCallToAction focusedAsset={focusedAsset} source={source} />;
  }

  return (
    <CallToAction
      text={{
        primary: appendSequenceNumbers(t('details:watch-now'), focusedAsset),
        secondary: t('common:from-price', { value: source.product.price?.amount ?? '???' }),
      }}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgCart />}
      onClick={() => setIsTransactionModalOpen(true)}
    />
  );
}

function OwnedVodCallToAction({ focusedAsset, source }: VodGuestCallToActionProps) {
  const { t } = useTranslation();

  if (source.bookmark !== undefined && source.bookmark.isWatched) {
    return <WatchedVodCallToAction focusedAsset={focusedAsset} source={source} />;
  }

  if (source.bookmark !== undefined) {
    return <ContinueWatchingButton focusedAsset={focusedAsset} source={source} bookmark={source.bookmark} />;
  }

  const url = routes.play({
    platformTitleId: source.platformTitleId,
    providerIdentifier: source.providerIdentifier,
  });

  return (
    <CallToAction
      text={appendSequenceNumbers(t('details:watch-now'), focusedAsset)}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgPlayFilled />}
      to={url}
    />
  );
}

function WatchedVodCallToAction({ focusedAsset, source }: VodGuestCallToActionProps) {
  const { t } = useTranslation();
  const progress = getProgress(source.bookmark, focusedAsset);
  const url = routes.play({
    platformTitleId: source.platformTitleId,
    providerIdentifier: source.providerIdentifier,
    position: 0,
  });
  return (
    <CallToAction
      text={appendSequenceNumbers(t('watch again'), focusedAsset)}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgPlayFilled />}
      to={url}
      progress={progress}
    />
  );
}

interface VodGuestCallToActionProps {
  focusedAsset: FocusedAsset;
  source: VodSource;
}

function VodGuestCallToAction({ focusedAsset, source }: VodGuestCallToActionProps) {
  const { t } = useTranslation();
  const location = useLocation();

  if (source.product.status === 'purchased' || source.product.status === 'rented') {
    throw new Error('Source product should not be purchased or rented for guests');
  }

  const url = routes.login(location);

  return (
    <CallToAction
      text={{
        primary: appendSequenceNumbers(t('details:watch-now'), focusedAsset),
        // TODO: Price might not exist, we don't know if this is a bug or not.
        //       Until we know, we won't update the types.
        secondary: t('common:from-price', { value: source.product.price?.amount ?? '???' }),
      }}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgCart />}
      to={url}
    />
  );
}
